import { Form, useFormikContext } from 'formik';
import { useEffect } from 'react';

import { applySetStateAction } from '@/react-tools/apply-set-state-action';
import { EnvironmentType } from '@/react/portainer/environments/types';

import { EnvironmentVariablesPanel } from '@@/form-components/EnvironmentVariablesFieldset';
import { FormActions } from '@@/form-components/FormActions';

import { EdgeGroupsSelector } from '../components/EdgeGroupsSelector';
import { EdgeStackDeploymentTypeSelector } from '../components/EdgeStackDeploymentTypeSelector';
import { StaggerFieldset } from '../components/StaggerFieldset';
import { PrivateRegistryFieldsetWrapper } from '../ItemView/EditEdgeStackForm/PrivateRegistryFieldsetWrapper';
import { useEdgeGroupHasType } from '../ItemView/EditEdgeStackForm/useEdgeGroupHasType';
import { DeploymentType } from '../types';

import { DockerComposeForm } from './DockerComposeForm';
import { KubeFormValues, KubeManifestForm } from './KubeManifestForm';
import { HelmChartForm } from './HelmChartForm/HelmChartForm';
import { NameField } from './NameField';
import { WebhookSwitch } from './WebhookSwitch';
import { FormValues } from './types';
import { DeploymentOptions } from './DeploymentOptions';

export function InnerForm({
  webhookId,
  isLoading,
  onChangeTemplate,
}: {
  webhookId: string;
  isLoading: boolean;
  onChangeTemplate: ({
    templateId,
    templateType,
  }: {
    templateId: number | undefined;
    templateType: 'app' | 'custom' | undefined;
  }) => void;
}) {
  const { values, setFieldValue, errors, setValues, setFieldError, isValid } =
    useFormikContext<FormValues>();
  const { hasType } = useEdgeGroupHasType(values.groupIds);

  const hasKubeEndpoint = hasType(EnvironmentType.EdgeAgentOnKubernetes);
  const hasDockerEndpoint = hasType(EnvironmentType.EdgeAgentOnDocker);
  const hasMultipleTypes = hasKubeEndpoint && hasDockerEndpoint;
  const multipleTypesError = hasMultipleTypes
    ? `There are no available deployment types when there is more than one
          type of environment in your edge group selection (e.g. Kubernetes and
          Docker environments). Please select edge groups that have environments
          of the same type.`
    : undefined;

  // Auto-select first available deployment type when current selection becomes disabled
  useEffect(() => {
    const isComposeDisabled = hasKubeEndpoint;
    const isKubernetesDisabled = hasDockerEndpoint;
    const isHelmDisabled = hasDockerEndpoint;

    const isCurrentTypeDisabled =
      (values.deploymentType === DeploymentType.Compose && isComposeDisabled) ||
      (values.deploymentType === DeploymentType.Kubernetes &&
        isKubernetesDisabled) ||
      (values.deploymentType === DeploymentType.KubernetesHelm &&
        isHelmDisabled);

    if (isCurrentTypeDisabled) {
      // Select first available deployment type in order: Compose, Kubernetes, Helm
      if (!isComposeDisabled) {
        setFieldValue('deploymentType', DeploymentType.Compose);
      } else if (!isKubernetesDisabled) {
        setFieldValue('deploymentType', DeploymentType.Kubernetes);
      } else if (!isHelmDisabled) {
        setFieldValue('deploymentType', DeploymentType.KubernetesHelm);
      }
    }
  }, [
    hasKubeEndpoint,
    hasDockerEndpoint,
    values.deploymentType,
    setFieldValue,
  ]);

  return (
    <Form className="form-horizontal" role="form">
      <NameField
        onChange={(value) => setFieldValue('name', value)}
        value={values.name}
        errors={errors.name}
        placeholder="e.g. my-stack"
      />

      <EdgeGroupsSelector
        value={values.groupIds}
        onChange={(value) => setFieldValue('groupIds', value)}
        error={errors.groupIds || multipleTypesError}
      />

      <EdgeStackDeploymentTypeSelector
        value={values.deploymentType}
        hasDockerEndpoint={hasDockerEndpoint}
        hasKubeEndpoint={hasKubeEndpoint}
        onChange={(value) => {
          setFieldValue('deploymentType', value);
          // Set method to 'repository' when Helm deployment type is selected
          if (value === DeploymentType.KubernetesHelm) {
            setFieldValue('method', 'repository');
          }
        }}
        error={errors.deploymentType}
      />

      {values.deploymentType === DeploymentType.Compose && (
        <DockerComposeForm
          webhookId={webhookId}
          onChangeTemplate={onChangeTemplate}
        />
      )}

      {values.deploymentType === DeploymentType.Kubernetes && (
        <KubeManifestForm
          values={values as KubeFormValues}
          webhookId={webhookId}
          errors={errors}
          setValues={(kubeValues) =>
            setValues((values) => ({
              ...values,
              ...applySetStateAction(kubeValues, values as KubeFormValues),
            }))
          }
        />
      )}

      {values.deploymentType === DeploymentType.KubernetesHelm && (
        <HelmChartForm webhookId={webhookId} />
      )}

      {values.method !== 'repository' && values.method !== 'helmRepository' && (
        <WebhookSwitch
          onChange={(value) => setFieldValue('enableWebhook', value)}
          value={values.enableWebhook}
        />
      )}

      {values.deploymentType === DeploymentType.Compose && (
        <EnvironmentVariablesPanel
          values={values.envVars}
          onChange={(value) => setFieldValue('envVars', value)}
        />
      )}

      {values.deploymentType !== DeploymentType.KubernetesHelm && (
        <PrivateRegistryFieldsetWrapper
          onChange={(value) => setFieldValue('privateRegistryId', value)}
          value={values.privateRegistryId}
          values={{ fileContent: values.fileContent, file: values.file }}
          error={errors.privateRegistryId}
          onFieldError={(message) =>
            setFieldError('privateRegistryId', message)
          }
          isGit={values.method === 'repository'}
        />
      )}

      {values.deploymentType === DeploymentType.Compose && (
        <DeploymentOptions values={values} setFieldValue={setFieldValue} />
      )}

      <StaggerFieldset
        isEdit={false}
        values={values.staggerConfig}
        errors={errors.staggerConfig}
        onChange={(newStaggerValues) =>
          setValues((values) => ({
            ...values,
            staggerConfig: {
              ...values.staggerConfig,
              ...newStaggerValues,
            },
          }))
        }
      />

      <FormActions
        data-cy="edgeStackCreate-createStackButton"
        submitLabel="Deploy the stack"
        loadingText="Deployment in progress..."
        isValid={isValid}
        isLoading={isLoading}
      />
    </Form>
  );
}
